<script setup>
import { ref, computed } from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import UiCard from '@/Components/UI/UiCard.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import SubRentalRow from '@/Components/Suppliers/SubRentalRow.vue'
import RecordSupplierPaymentModal from '@/Components/Suppliers/RecordSupplierPaymentModal.vue'
import CustomerChannels from '@/Components/CustomerChannels.vue'
import { useI18n } from 'vue-i18n'
import { Link, usePage, router } from '@inertiajs/vue3'

defineOptions({ layout: AppLayout })

const props = defineProps({
  source:         { type: Object, required: true },
  sub_rentals:    { type: Array,  default: () => [] },
  channels:       { type: Array,  default: () => [] },
  conversations:  { type: Array,  default: () => [] },
  portal_account: { type: Object, default: null }
})

function fmtRelative(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
}

const { t } = useI18n()
const isAdmin = usePage().props.auth?.user?.role === 'admin'

const paymentModalOpen = ref(false)
const activeSubRental  = ref(null)

function recordPayment(sr) {
  activeSubRental.value = sr
  paymentModalOpen.value = true
}

function archive() {
  if (!confirm(t('suppliers.show.archiveConfirm'))) return
  router.delete(`/vehicle_sources/${props.source.id}`)
}

const hasPersonDetails = computed(() => {
  const s = props.source
  return Boolean(
    s.first_name || s.last_name || s.date_of_birth ||
    s.nationality || s.passport_no ||
    s.licence_number || s.licence_expiry || s.address ||
    s.licence_scan_url || s.passport_photo_url
  )
})
</script>

<template>
  <PageShell :title="source.name" back-href="/vehicle_sources" :back-label="t('common.back')">
    <template #actions>
      <Link :href="`/vehicles/new?vehicle_source_id=${source.id}`">
        <UiButton variant="primary" size="sm" icon-left="plus">
          {{ t('suppliers.show.addVehicle') }}
        </UiButton>
      </Link>
      <Link v-if="isAdmin" :href="`/vehicle_sources/${source.id}/edit`">
        <UiButton variant="ghost" size="sm">
          {{ t('common.edit') }}
        </UiButton>
      </Link>
      <UiButton v-if="isAdmin" variant="danger" size="sm" @click="archive">
        {{ t('suppliers.show.archive') }}
      </UiButton>
    </template>

    <UiCard>
      <div class="flex flex-wrap items-baseline gap-4">
        <div>
          <div class="text-xs text-[color:var(--muted)]">{{ t('suppliers.show.outstanding') }}</div>
          <div
            class="fw-value tabular-nums text-2xl"
            :class="source.outstanding_balance_cents > 0 ? 'text-[color:var(--warn)]' : ''"
          >
            {{ source.outstanding_balance }}
          </div>
        </div>
        <div class="ml-auto text-sm text-[color:var(--inkSoft)] space-y-1">
          <div v-if="source.contact_phone">📞 {{ source.contact_phone }}</div>
          <div v-if="source.contact_email">✉ {{ source.contact_email }}</div>
        </div>
      </div>
      <p v-if="source.notes" class="mt-3 text-sm text-[color:var(--inkSoft)] whitespace-pre-line">
        {{ source.notes }}
      </p>
    </UiCard>

    <UiCard v-if="hasPersonDetails">
      <h3 class="text-sm font-semibold mb-3">Personal details</h3>
      <dl class="grid grid-cols-2 gap-y-2 text-sm">
        <template v-if="source.first_name || source.last_name">
          <dt class="text-[color:var(--muted)]">Name</dt>
          <dd class="fw-value text-[color:var(--ink)]">{{ source.full_name }}</dd>
        </template>
        <template v-if="source.date_of_birth">
          <dt class="text-[color:var(--muted)]">Date of birth</dt>
          <dd class="fw-value text-[color:var(--ink)]">{{ source.date_of_birth }}</dd>
        </template>
        <template v-if="source.nationality">
          <dt class="text-[color:var(--muted)]">Nationality</dt>
          <dd class="fw-value text-[color:var(--ink)]">{{ source.nationality }}</dd>
        </template>
        <template v-if="source.passport_no">
          <dt class="text-[color:var(--muted)]">Passport number</dt>
          <dd class="fw-value text-[color:var(--ink)] font-mono">{{ source.passport_no }}</dd>
        </template>
        <template v-if="source.licence_number">
          <dt class="text-[color:var(--muted)]">Licence number</dt>
          <dd class="fw-value text-[color:var(--ink)] font-mono">{{ source.licence_number }}</dd>
        </template>
        <template v-if="source.licence_expiry">
          <dt class="text-[color:var(--muted)]">Licence expiry</dt>
          <dd class="fw-value text-[color:var(--ink)]">{{ source.licence_expiry }}</dd>
        </template>
        <template v-if="source.address">
          <dt class="text-[color:var(--muted)] col-span-2">Address</dt>
          <dd class="text-[color:var(--ink)] whitespace-pre-line col-span-2">{{ source.address }}</dd>
        </template>
        <template v-if="source.licence_scan_url">
          <dt class="text-[color:var(--muted)]">Licence scan</dt>
          <dd>
            <a :href="source.licence_scan_url" target="_blank" class="text-[color:var(--accent)] hover:underline text-sm">View document</a>
          </dd>
        </template>
        <template v-if="source.passport_photo_url">
          <dt class="text-[color:var(--muted)]">Passport photo</dt>
          <dd>
            <a :href="source.passport_photo_url" target="_blank" class="text-[color:var(--accent)] hover:underline text-sm">View document</a>
          </dd>
        </template>
      </dl>
    </UiCard>

    <UiCard>
      <h3 class="text-sm font-semibold mb-3">{{ t('suppliers.show.subRentals') }}</h3>
      <div v-if="sub_rentals.length === 0" class="text-sm text-[color:var(--muted)] py-6 text-center">
        {{ t('suppliers.show.noSubRentals') }}
      </div>
      <table v-else class="w-full text-sm">
        <thead class="text-xs uppercase tracking-wider text-[color:var(--muted)]">
          <tr>
            <th class="text-left py-2">{{ t('sub_rentals.cols.vehicle') }}</th>
            <th class="text-left py-2">{{ t('sub_rentals.cols.period') }}</th>
            <th class="text-right py-2">{{ t('sub_rentals.cols.rate') }}</th>
            <th class="text-right py-2">{{ t('sub_rentals.cols.planned') }}</th>
            <th class="text-right py-2">{{ t('sub_rentals.cols.paid') }}</th>
            <th class="text-right py-2">{{ t('sub_rentals.cols.balance') }}</th>
            <th class="text-left py-2">{{ t('sub_rentals.cols.status') }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <SubRentalRow
            v-for="sr in sub_rentals"
            :key="sr.id"
            :sub-rental="sr"
            @record-payment="recordPayment"
          />
        </tbody>
      </table>
    </UiCard>

    <UiCard v-if="channels.length">
      <CustomerChannels :model-value="channels" :readonly="true" />
    </UiCard>

    <UiCard>
      <div class="flex items-center justify-between mb-3">
        <h3 class="text-sm font-semibold">{{ t('suppliers.show.conversations') }}</h3>
        <Link
          href="/inbox"
          class="text-xs text-[color:var(--accent)] underline"
        >
          {{ t('suppliers.show.openInbox') }}
        </Link>
      </div>
      <div v-if="conversations.length === 0" class="text-sm text-[color:var(--muted)] py-6 text-center">
        {{ t('suppliers.show.noConversations') }}
      </div>
      <ul v-else class="divide-y divide-[color:var(--line)]">
        <li v-for="c in conversations" :key="c.id">
          <Link
            :href="`/inbox/conversations/${c.id}`"
            class="flex items-center gap-3 py-2 hover:bg-[color:var(--surfaceAlt)] -mx-2 px-2 rounded"
          >
            <span class="text-xs uppercase tracking-wider text-[color:var(--muted)] w-24 truncate">
              {{ (c.channel_type || '').replace(/^Channel::/, '') }}
            </span>
            <span class="flex-1 text-sm text-[color:var(--ink)] truncate">
              {{ c.contact_name || '—' }}
            </span>
            <span
              v-if="c.unread > 0"
              class="text-[10px] px-1.5 py-0.5 rounded-full"
              :style="{ background: 'var(--accentSoft)', color: 'var(--accent)' }"
            >
              {{ c.unread }}
            </span>
            <span class="text-xs text-[color:var(--muted)]">
              {{ fmtRelative(c.last_message_at) }}
            </span>
          </Link>
        </li>
      </ul>
    </UiCard>

    <!-- Portal access -->
    <UiCard v-if="isAdmin">
      <h3 class="text-sm font-semibold mb-3">{{ t('suppliers.show.portalAccess') }}</h3>
      <div v-if="portal_account" class="text-sm space-y-1">
        <div class="flex flex-wrap items-center gap-2">
          <span class="fw-value text-[color:var(--ink)]">{{ portal_account.email }}</span>
          <span
            class="px-2 py-0.5 rounded-full text-xs font-medium"
            :style="portal_account.status === 'active'
              ? { background: 'color-mix(in oklch, var(--ok) 14%, var(--surface))', color: 'var(--ok)' }
              : { background: 'color-mix(in oklch, var(--muted) 14%, var(--surface))', color: 'var(--muted)' }"
          >
            {{ portal_account.status }}
          </span>
        </div>
        <div v-if="portal_account.last_sign_in_at" class="text-xs text-[color:var(--muted)]">
          {{ t('suppliers.show.lastSignIn') }}: {{ fmtRelative(portal_account.last_sign_in_at) }}
        </div>
        <div v-else class="text-xs text-[color:var(--muted)]">
          {{ t('suppliers.show.neverSignedIn') }}
        </div>
        <div class="mt-2">
          <Link
            :href="`/admin/portal_accounts/${portal_account.id}`"
            class="text-sm text-[color:var(--accent)] hover:underline"
          >
            {{ t('suppliers.show.managePortal') }} →
          </Link>
        </div>
      </div>
      <div v-else class="text-sm text-[color:var(--muted)]">
        <p class="mb-2">{{ t('suppliers.show.noPortalAccount') }}</p>
        <Link
          :href="`/admin/portal_accounts/new?owner_type=VehicleSource&owner_id=${source.id}`"
          class="text-sm text-[color:var(--accent)] hover:underline"
        >
          + {{ t('suppliers.show.provisionPortal') }}
        </Link>
      </div>
    </UiCard>

    <RecordSupplierPaymentModal
      v-model:open="paymentModalOpen"
      :sub-rental="activeSubRental"
    />
  </PageShell>
</template>
